import React, { memo, useMemo } from "react";
import { MessageSegment } from "../utils/messages";
import { cn } from "@/lib/utils";
import { Search, Files, Brain } from "lucide-react";
import { PlanOfActionIcon } from "@/components/RightStyle/TailwindStylingPanel/components/icons";

interface ParsedText {
  type: "text" | "bold" | "italic";
  content: string;
}

const parseMarkdown = (text: string): ParsedText[] => {
  const parts: ParsedText[] = [];
  let currentText = "";
  let i = 0;

  while (i < text.length) {
    if (text.startsWith("**", i)) {
      if (currentText) {
        parts.push({ type: "text", content: currentText });
        currentText = "";
      }
      i += 2;
      let boldText = "";
      while (i < text.length && !text.startsWith("**", i)) {
        boldText += text[i];
        i++;
      }
      i += 2;
      parts.push({ type: "bold", content: boldText });
    } else if (text.startsWith("*", i)) {
      if (currentText) {
        parts.push({ type: "text", content: currentText });
        currentText = "";
      }
      i++;
      let italicText = "";
      while (i < text.length && text[i] !== "*") {
        italicText += text[i];
        i++;
      }
      i++;
      parts.push({ type: "italic", content: italicText });
    } else {
      currentText += text[i];
      i++;
    }
  }

  if (currentText) {
    parts.push({ type: "text", content: currentText });
  }

  return parts;
};

interface ChatMessageProps {
  block: MessageSegment;
}

export const ReasoningChatMessage = memo(({ block }: ChatMessageProps) => {
  const { parsedReasoningLines, searchSummary, planOfAction, affectedFiles } =
    useMemo(() => {
      try {
        const parsedJson = block.parsedJson;
        let reasoning = parsedJson?.reasoning || "";
        let search = parsedJson?.summary_of_search_results || "";
        let plan = parsedJson?.plan_of_action || "";
        let files: string[] = parsedJson?.affected_files || [];

        // Perform the rendering inside the
        const parsedReasoningLines = reasoning.split("\n").map((line) => {
          return parseMarkdown(line);
        });

        return {
          parsedReasoningLines,
          searchSummary: search,
          planOfAction: plan,
          affectedFiles: files,
        };
      } catch (error) {
        console.error("Failed to parse reasoning JSON:", error);
        return {
          parsedReasoningLines: [],
          searchSummary: "",
          planOfAction: "",
          affectedFiles: [],
        };
      }
    }, [block.parsedJson]);

  return (
    <div
      className={cn(
        "flex flex-col grow bg-secondary-200 px-1 pt-3 pb-3 rounded-md gap-3"
      )}
    >
      {parsedReasoningLines &&
        parsedReasoningLines.length > 0 &&
        parsedReasoningLines.some((line) => line.length > 0) && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 items-center justify-start px-2">
              <Brain className="h-4 w-4 text-secondary-n-50" />
              <span className="text-xs text-secondary-n-50 font-medium">
                Reasoning
              </span>
            </div>
            <div className="px-2 pt-2">
              <div
                className={cn(
                  " text-xs py-0.5 text-secondary-n-400 break-all whitespace-pre-line"
                )}
              >
                {parsedReasoningLines
                  .map((line, lineIndex) => {
                    return line.map((part, partIndex) => {
                      switch (part.type) {
                        case "bold":
                          return (
                            <strong key={`${lineIndex}-${partIndex}`}>
                              {part.content}
                            </strong>
                          );
                        case "italic":
                          return (
                            <em key={`${lineIndex}-${partIndex}`}>
                              {part.content}
                            </em>
                          );
                        default:
                          return (
                            <span key={`${lineIndex}-${partIndex}`}>
                              {part.content}
                            </span>
                          );
                      }
                    });
                  })
                  .map((line, i) => {
                    return (
                      <React.Fragment key={i}>
                        <span>{line}</span>
                        <span>
                          {i < parsedReasoningLines.length - 1 && <br />}
                        </span>
                      </React.Fragment>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

      {searchSummary &&
        typeof searchSummary === "string" &&
        searchSummary.trim() && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 items-center justify-start px-2 pb-2">
              <Search className="size-4 shrink-0 text-secondary-n-50" />
              <span className="text-xs text-secondary-n-50">
                Search Summary
              </span>
            </div>
            <div className="px-2">
              <div
                className={cn(
                  "text-xs font-mono py-0.5 px-2  text-secondary-n-400 break-all whitespace-pre-line"
                )}
              >
                {searchSummary.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    <span>{line}</span>
                    <span>
                      {i < searchSummary.split("\n").length - 1 && <br />}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

      {planOfAction &&
        typeof planOfAction === "string" &&
        planOfAction.trim() && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 items-center justify-start px-2 pb-2">
              <PlanOfActionIcon className="h-4 w-4 text-secondary-n-50 rotate-180" />
              <span className="text-xs text-secondary-n-50 font-medium">
                Plan of Action
              </span>
            </div>
            <div className="px-2">
              <div
                className={cn(
                  "text-xs py-0.5 text-secondary-n-400 break-all whitespace-pre-line"
                )}
              >
                {planOfAction.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    <span>{line}</span>
                    <span>
                      {i < planOfAction.split("\n").length - 1 && <br />}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

      {Array.isArray(affectedFiles) &&
        affectedFiles.filter((file) => typeof file === "string" && file.trim())
          .length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 items-center justify-start px-2 pb-2">
              <Files className="size-4 shrink-0 text-secondary-n-50" />
              <span className="text-xs text-secondary-n-50 font-medium">
                Affected Files
              </span>
            </div>
            <div className="px-2">
              <div
                className={cn("text-xs py-0.5 text-secondary-n-400 break-all")}
              >
                {affectedFiles
                  .filter((file) => typeof file === "string" && file.trim())
                  .join(", ")}
              </div>
            </div>
          </div>
        )}
    </div>
  );
});
